import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

import type { AccountPreferences } from "./useAccountPreferences";

async function updateAccountPreferencesRequest(
  workspaceId: string,
  accountId: string,
  autoaccept: boolean,
): Promise<AccountPreferences> {
  const { preferences } = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/preferences`,
    { autoaccept },
  );
  return preferences;
}

export default function useAccountPreferencesMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { preferences: preferencesQueryKey } =
    getQueryKeys(workspaceId).accountsKeys;

  const { mutateAsync: updateAccountPreferences } = useMutation({
    mutationFn: ({
      accountId,
      autoaccept,
    }: {
      accountId: string;
      autoaccept: boolean;
    }) => updateAccountPreferencesRequest(workspaceId, accountId, autoaccept),
    onMutate: ({ accountId, autoaccept }) => {
      const previousPreferences = queryClient.getQueryData<AccountPreferences>(
        preferencesQueryKey(accountId),
      );

      queryClient.setQueryData(preferencesQueryKey(accountId), {
        ...previousPreferences,
        autoaccept,
      });

      return previousPreferences;
    },
    onError: (_, { accountId }, previousPreferences) => {
      queryClient.setQueryData(
        preferencesQueryKey(accountId),
        previousPreferences,
      );
    },
  });

  return {
    updateAccountPreferences,
  };
}
